/**
 * Code-file approval (owner 2026-10-27, #1452) — the ONE "Approve this code?" flow, for every surface that shows a code
 * file needing approval (the drive's file list, the workspace explorer). A confirm that says what approving means, the
 * Approve call naming the content the row listed (`expectedContentHash`), and the answers: approved, or 409 — the file
 * changed since it was listed (nothing approved; the caller re-reads it so the person reviews the new version).
 */
import { useCallback } from 'react'
import { useConfirm, useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { filesApi } from '@papan/files/client'

export interface ApprovableCodeFile {
  id: string
  name: string
  /** The content the row listed — the version the person is approving. */
  contentHash?: string | null
}

/** Returns `approve(file)`: true when approved, false when cancelled or refused. `onChanged` runs after an approval and
 *  after a 409 (re-read the listing either way). */
export function useApproveCode(onChanged?: (file: ApprovableCodeFile) => void): (file: ApprovableCodeFile) => Promise<boolean> {
  const confirm = useConfirm()
  const toast = useToast()
  const { t } = useTranslation('files')
  return useCallback(async (file: ApprovableCodeFile) => {
    const ok = await confirm({
      title: t('driveView.code.confirmTitle'),
      message: t('driveView.code.confirmMessage', { name: file.name }),
      type: 'warning',
      confirmText: t('driveView.code.confirmOk'),
    })
    if (!ok) return false
    try {
      await filesApi.approveCode(file.id, file.contentHash ?? null)
      toast.success(t('driveView.code.approved', { name: file.name }))
      onChanged?.(file)
      return true
    } catch (err: unknown) {
      // 409 code_changed: the file was rewritten after this listing — show the new version before anyone approves it
      if ((err as { status?: number } | null)?.status === 409) {
        toast.error(t('driveView.code.changed', { name: file.name }))
        onChanged?.(file)
        return false
      }
      toast.error(t('driveView.code.approveFailed'))
      return false
    }
  }, [confirm, toast, t, onChanged])
}
